import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/**
 * Hook pour surveiller les mises à jour de contrats et déclencher un refresh automatique
 * @param {function} onUpdate - Callback appelé quand un contrat est mis à jour
 * @param {number} pollInterval - Intervalle de polling en ms (défaut: 30s)
 * @param {boolean} enabled - Active/désactive le polling
 */
export const useContratAutoRefresh = (onUpdate, pollInterval = 30000, enabled = true) => {
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isConnected, setIsConnected] = useState(true); // Optimiste par défaut
  const [lastPollTime, setLastPollTime] = useState(null);
  const lastKnownUpdate = useRef(null);
  const failCount = useRef(0);

  const checkForUpdates = useCallback(async () => {
    if (!enabled) return;
    
    try {
      setIsPolling(true);
      const response = await api.get('/contrats/last-update');
      const newUpdate = response.data?.last_update;
      
      setIsConnected(true);
      setLastPollTime(new Date());
      failCount.current = 0; // Reset fail counter on success
      
      if (newUpdate && lastKnownUpdate.current && newUpdate !== lastKnownUpdate.current) {
        // Il y a eu une mise à jour !
        console.log('[AutoRefresh] Nouvelle mise à jour de contrat détectée:', response.data);
        setLastUpdate(newUpdate);
        if (onUpdate) {
          onUpdate(response.data);
        }
      }
      
      lastKnownUpdate.current = newUpdate;
    } catch (error) {
      console.error('[AutoRefresh] Erreur lors du check:', error);
      failCount.current += 1;
      // Ne passer à "Hors ligne" qu'après 3 échecs consécutifs
      if (failCount.current >= 3) {
        setIsConnected(false);
      }
    } finally {
      setIsPolling(false);
    }
  }, [enabled, onUpdate]);

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      return;
    }

    // Premier check immédiat pour initialiser
    checkForUpdates();

    // Setup polling interval
    const interval = setInterval(checkForUpdates, pollInterval);

    return () => {
      clearInterval(interval);
    };
  }, [enabled, pollInterval, checkForUpdates]);

  return { lastUpdate, isPolling, isConnected, lastPollTime };
};

export default useContratAutoRefresh;
